// src/pages/Swap/hooks/useTokenApproval.js - Fixed with proper transaction confirmation timing
/* global BigInt */

import { useState, useEffect, useContext, useCallback } from 'react';
import { ethers, Contract } from 'ethers';
import { checkAllowance, isNativeToken } from '../utils/SwapUtils';
import { ERC20_ABI } from '../utils/SwapConstants';
import { TransactionContext, TRANSACTION_TYPES } from '../../../contexts/TransactionContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { isUsingSushiSwap } from '../../../services/sushiswapApi';
import { useTranslation } from 'react-i18next';

/**
 * Hook for checking and handling token approvals with improved detection
 * @param {function} t - Translation function (optional)
 */
export const useTokenApproval = (
  provider, 
  currentWallet, 
  userAddress, 
  inputToken, 
  currentChain,
  amountIn, 
  explorerUrl,
  notificationRef,
  autoConfirm,
) => {
  const { t } = useTranslation();
  // Get the actual aggregator address based on chain config
  const aggregatorAddress = isUsingSushiSwap(currentChain) 
    ? currentChain?.sushiswapConfig?.routerAddress // Use router address for SushiSwap
    : currentChain?.aggregatorAddress; // Use regular aggregator address otherwise

  const [needsApproval, setNeedsApproval] = useState(false);
  const [isCheckingApproval, setIsCheckingApproval] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const [lastCheckedTimestamp, setLastCheckedTimestamp] = useState(0);
  const [approvalConfirmationModalOpen, setApprovalConfirmationModalOpen] = useState(false);
  const [approvalConfirmationDetails, setApprovalConfirmationDetails] = useState(null);
  const [approvalTxnHash, setApprovalTxnHash] = useState(null);
  const [isPendingConfirmation, setIsPendingConfirmation] = useState(false);

  // Get transaction context functions
  const { addTransaction, updateTransactionStatus } = useContext(TransactionContext);
  // Get current chain for correct network identification
  const { currentChain: contextChain } = useContext(NetworkContext);

  // Function to check approval status - extracted so it can be called manually
  const checkApprovalStatus = useCallback(async (force = false) => {
    // Skip approval check if we don't have what we need
    if (!provider || !userAddress || !inputToken.symbol || !aggregatorAddress) {
      setNeedsApproval(false);
      return;
    }

    // Native tokens (ETH, BROCK, etc.) don't need approval
    if (isNativeToken(inputToken.address)) {
      setNeedsApproval(false);
      return;
    }

    // Skip if we checked recently (within last 10 seconds) unless forced
    const now = Date.now();
    if (!force && (now - lastCheckedTimestamp < 10000)) {
      console.log("Skipping approval check - checked recently");
      return;
    }

    setIsCheckingApproval(true);
    setLastCheckedTimestamp(now);

    try {
      console.log(`Checking allowance for ${inputToken.symbol} to spender ${aggregatorAddress}`);
      
      // Direct check of allowance for debugging purposes
      if (inputToken.address) {
        try {
          const tokenContract = new Contract(
            inputToken.address,
            ["function allowance(address,address) view returns (uint256)"],
            provider
          );
          
          const rawAllowance = await tokenContract.allowance(userAddress, aggregatorAddress);
          console.log(`Raw allowance for ${inputToken.symbol}: ${rawAllowance.toString()}`);
        } catch (directError) {
          console.error("Error in direct allowance check:", directError);
        }
      }
      
      const isApproved = await checkAllowance(provider, userAddress, inputToken, aggregatorAddress, amountIn);
      console.log(`Approval status for ${inputToken.symbol}: ${isApproved ? 'Approved' : 'Not approved'}`);
      setNeedsApproval(!isApproved);
    } catch (error) {
      console.error("Error checking allowance:", error);
      // Default to needing approval if we can't check
      setNeedsApproval(true);
    } finally {
      setIsCheckingApproval(false);
    }
  }, [provider, userAddress, inputToken, aggregatorAddress, amountIn, lastCheckedTimestamp]);

  // Manual refresh function for users to trigger when approval isn't detected
  const refreshApprovalStatus = useCallback(() => {
    console.log("Manually refreshing approval status");
    return checkApprovalStatus(true);
  }, [checkApprovalStatus]);

  // Check approval when input token or amount changes
  useEffect(() => {
    checkApprovalStatus();
  }, [checkApprovalStatus, inputToken.address, inputToken.symbol, aggregatorAddress, amountIn]);

  /**
   * Prepare the approval confirmation details for the modal
   */
  const prepareApprovalConfirmation = async () => {
    // Skip if we don't have what we need
    if (!provider || !currentWallet || !userAddress || !aggregatorAddress) {
      return false;
    }
    
    // Native tokens don't need approval
    if (isNativeToken(inputToken.address)) {
      return false; // Already "approved"
    }
    
    // Verify wallet has privateKey
    if (!currentWallet.privateKey) {
      setApprovalError(t('swap.errors.walletConnectionIssue'));
      return false;
    }

    try {
      // Create wallet instance to get accurate address
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      const walletAddress = wallet.address;

      // Get the name of the exchange/router
      const routerName = isUsingSushiSwap(currentChain) 
        ? "Exchange Router"
        : "Exchange Aggregator";

      // Estimate gas cost (rough estimate) - FIXED: proper BigInt conversion
      const gasEstimate = BigInt(100000); // 100k gas for ERC20 approve
      const gasPrice = await provider.getFeeData()
        .then(data => data.gasPrice || ethers.parseUnits("3", "gwei"))
        .catch(() => ethers.parseUnits("3", "gwei"));

      // FIXED: Both values are now BigInt so multiplication is safe
      const gasCost = gasEstimate * gasPrice;
      const gasCostFormatted = ethers.formatEther(gasCost);
      
      // Prepare confirmation details
      const details = {
        type: t('swap.approval.tokenApproval'),
        from: walletAddress,
        to: inputToken.address,
        spendingAmount: t('swap.approval.unlimited', { symbol: inputToken.symbol }),
        receivingAmount: t('swap.approval.approvalFor', { symbol: inputToken.symbol }),
        routingInfo: t('swap.approval.router', { name: routerName }),
        gasFee: `${parseFloat(gasCostFormatted).toFixed(8)} ${currentChain?.nativeToken?.symbol || 'ETH'}`,
      };

      // Reset states when opening a new confirmation
      setApprovalTxnHash(null);
      setIsPendingConfirmation(false);
      setApprovalConfirmationDetails(details);
      setApprovalConfirmationModalOpen(true);
      return true;
    } catch (error) {
      console.error("Error preparing approval:", error);
      setApprovalError(t('swap.errors.failedToPrepareTx', { message: error.message }));
      return false;
    }
  };

  /**
   * Execute the token approval after confirmation
   */
  const executeApproval = async (gasSettings = {}) => {
    // Skip if we don't have what we need
    if (!provider || !currentWallet || !userAddress || !aggregatorAddress) {
      setApprovalError(t('swap.errors.missingApprovalData'));
      setApprovalConfirmationModalOpen(false); // Close modal on error
      return false;
    }
    
    // Native tokens don't need approval
    if (isNativeToken(inputToken.address)) {
      return true; // Already "approved"
    }
    
    // Verify wallet has privateKey
    if (!currentWallet.privateKey) {
      setApprovalError(t('swap.errors.walletConnectionIssue'));
      setApprovalConfirmationModalOpen(false); // Close modal on error
      return false;
    }
    
    setIsApproving(true);
    setApprovalError(null);
    
    try {
      // Create wallet instance
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      
      const tokenContract = new Contract(
        inputToken.address,
        ERC20_ABI,
        wallet
      );
      
      // Approve max uint256 amount
      const maxUint256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935';
      
      // Prepare transaction options with gas settings
      const txOptions = {};
      
      // Apply gas settings if provided
      if (gasSettings?.gasOption === 'custom' && gasSettings?.gasPrice) {
        try {
          txOptions.gasPrice = ethers.parseUnits(gasSettings.gasPrice, 'gwei');
          console.log(`Using custom gas price for approval: ${gasSettings.gasPrice} Gwei`);
        } catch (error) {
          console.error("Error setting custom gas price for approval:", error);
        }
      }
      
      console.log(`Approving ${inputToken.symbol} to spender ${aggregatorAddress}`);
      const tx = await tokenContract.approve(aggregatorAddress, maxUint256, txOptions);
      
      // Mark as pending confirmation but don't set the hash yet for success view
      // This will keep the modal in "processing" state
      setIsPendingConfirmation(true);
      
      // Add the transaction to the context with 'pending' status
      addTransaction({
        hash: tx.hash,
        type: TRANSACTION_TYPES.APPROVE,
        from: currentWallet.address,
        to: inputToken.address,
        value: "Unlimited",
        tokenSymbol: inputToken.symbol,
        tokenDecimal: inputToken.decimals || 18,
        status: 'pending',
        // Use currentChain.key directly to match the filter in Transactions.js
        network: contextChain?.key || 'unknown',
        data: JSON.stringify({
          spender: aggregatorAddress,
          method: "approve"
        }),
        explorerUrl: explorerUrl ? `${explorerUrl}/tx/${tx.hash}` : ''
      });
      
      console.log("Added approval transaction to history:", {
        hash: tx.hash,
        type: TRANSACTION_TYPES.APPROVE,
        network: contextChain?.key
      });
      
      // Show notification ONLY if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showProcessing(explorerUrl || '');
      }
      
      console.log("Approval transaction sent:", tx.hash);
      
      // Store pending transaction hash for reference but don't update the modal yet
      const pendingTxHash = tx.hash;
      
      // Wait for confirmation - this is the key part that ensures we don't show success too early
      const receipt = await tx.wait();
      console.log("Approval confirmed:", receipt);
      
      // Now that the transaction is confirmed, set the hash to update the modal
      setApprovalTxnHash(pendingTxHash);
      setIsPendingConfirmation(false);
      
      // Update transaction status in context to 'success'
      updateTransactionStatus(pendingTxHash, 'success');
      
      // Update notification ONLY if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.showSuccess(receipt.hash, explorerUrl || '');
      }
      
      // Immediately check approval status again
      await refreshApprovalStatus();
      
      // Update approval status
      setNeedsApproval(false);
      setIsApproving(false);
      
      // Return success status with hash
      return { success: true, hash: receipt.hash };
    } catch (err) {
      console.error("Error approving token:", err);
      
      // Try to provide a more user-friendly error message
      let errorMessage = err.message || "Unknown error";
      if (errorMessage.includes("insufficient funds")) {
        errorMessage = t('swap.errors.insufficientFundsApprove');
      }
      
      // If we have a transaction hash but it failed, update the status
      if (err.transaction && err.transaction.hash) {
        updateTransactionStatus(err.transaction.hash, 'failed');
      }
      
      setApprovalError(t('swap.errors.approvalFailed', { message: errorMessage }));
      setIsApproving(false);
      setIsPendingConfirmation(false);
      
      // IMPORTANT: Close the modal when an error occurs
      // This ensures the user can see the error message
      setApprovalConfirmationModalOpen(false);
      
      // Hide notification ONLY if auto-confirm is enabled
      if (autoConfirm && notificationRef.current) {
        notificationRef.current.hide();
      }
      
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Function that's exposed to Swap component to initiate approval
   */
  const approveToken = async () => {
    // For auto-confirm, skip the confirmation modal
    if (autoConfirm) {
      return executeApproval();
    } else {
      // Otherwise show confirmation modal first
      return prepareApprovalConfirmation();
    }
  };

  return {
    needsApproval,
    isCheckingApproval,
    isApproving,
    approvalError,
    approvalConfirmationModalOpen,
    approvalConfirmationDetails,
    approvalTxnHash,
    isPendingConfirmation, // Expose pending state for modal
    approveToken,
    executeApproval,
    setApprovalConfirmationModalOpen,
    refreshApprovalStatus
  };
};

export default useTokenApproval;